import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/wheelDatePicker.css';

export interface WheelDatePickerProps {
  _value?: string;
  _onChange?: (value: string) => void;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

export const WheelDatePicker: React.FC<WheelDatePickerProps> = ({ value, onChange, onClose }) => {
  const currentYear = new Date().getFullYear();
  const [month, setMonth] = useState(1);
  const [day, setDay] = useState(1);
  const [year, setYear] = useState(currentYear - 20);

  const monthRef = useRef<HTMLDivElement>(null);
  const dayRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const itemHeight = 46;
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (value) {
      const [m, d, y] = value.split('/').map(Number);
      if (m && d && y) {
        setMonth(m);
        setDay(d);
        setYear(y);

        requestAnimationFrame(() => {
          if (monthRef.current) scrollToValue(monthRef.current, m);
          if (dayRef.current) scrollToValue(dayRef.current, d);
          if (yearRef.current) scrollToValue(yearRef.current, y);
        });
      }
    }
  }, [value]);

  const scrollToValue = (container: HTMLElement, value: number) => {
    const index = Array.from(container.children).findIndex(
      child => Number(child.getAttribute('data-value')) === value
    );
    if (index !== -1) {
      container.scrollTop = index * itemHeight;
    }
  };

  const handleScrollEnd = useCallback(
    (container: HTMLDivElement, values: number[], setValue: (value: number) => void) => {
      const scrollTop = container.scrollTop;
      const index = Math.round(scrollTop / itemHeight);
      const value = values[Math.min(index, values.length - 1)];

      setValue(value);
      container.scrollTop = index * itemHeight;
    },
    [itemHeight]
  );

  const handleScroll = useCallback(
    (container: HTMLDivElement, values: number[], setValue: (value: number) => void) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        handleScrollEnd(container, values, setValue);
      }, 150);
    },
    [handleScrollEnd]
  );

  const handleConfirm = useCallback(() => {
    onChange(`${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`);
    onClose();
  }, [month, day, year, onChange, onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleItemClick = (
    container: HTMLDivElement,
    value: number,
    setValue: (value: number) => void
  ) => {
    setValue(value);
    scrollToValue(container, value);
  };

  return (
    <div className="wheel-picker-overlay" onClick={onClose}>
      <div className="wheel-picker-container" onClick={e => e.stopPropagation()}>
        <div className="wheel-picker-header">
          <button className="wheel-picker-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="wheel-picker-confirm" onClick={handleConfirm}>
            OK
          </button>
        </div>
        <div className="wheel-picker-wheels">
          <div className="wheel-picker-wheel">
            <div className="wheel-picker-selection" />
            <div
              className="wheel-picker-items"
              ref={monthRef}
              onScroll={e => handleScroll(e.currentTarget, months, setMonth)}
            >
              {months.map(m => (
                <div
                  key={m}
                  className={`wheel-picker-item ${m === month ? 'selected' : ''}`}
                  data-value={m}
                  onClick={() => monthRef.current && handleItemClick(monthRef.current, m, setMonth)}
                >
                  {m.toString().padStart(2, '0')}
                </div>
              ))}
            </div>
          </div>
          <div className="wheel-picker-wheel">
            <div className="wheel-picker-selection" />
            <div
              className="wheel-picker-items"
              ref={dayRef}
              onScroll={e => handleScroll(e.currentTarget, days, setDay)}
            >
              {days.map(d => (
                <div
                  key={d}
                  className={`wheel-picker-item ${d === day ? 'selected' : ''}`}
                  data-value={d}
                  onClick={() => dayRef.current && handleItemClick(dayRef.current, d, setDay)}
                >
                  {d.toString().padStart(2, '0')}
                </div>
              ))}
            </div>
          </div>
          <div className="wheel-picker-wheel">
            <div className="wheel-picker-selection" />
            <div
              className="wheel-picker-items"
              ref={yearRef}
              onScroll={e => handleScroll(e.currentTarget, years, setYear)}
            >
              {years.map(y => (
                <div
                  key={y}
                  className={`wheel-picker-item ${y === year ? 'selected' : ''}`}
                  data-value={y}
                  onClick={() => yearRef.current && handleItemClick(yearRef.current, y, setYear)}
                >
                  {y}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
